// Community Favorites — Flickity slider of "look" cells that expand into a product panel.
// Requires the global Flickity (loaded from flickity.pkgd.min.js before this script).

if (!customElements.get('community-favorites')) {
  class CommunityFavorites extends HTMLElement {
    constructor() {
      super();
      this.flickity = null;
      this.initialized = false;
      this.onOpenClick = this.onOpenClick.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('.cf-track');
      if (!this.track || this.track.querySelectorAll('.cf-cell').length === 0) return;
      this.initWhenReady();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      if (this.flickity) {
        this.flickity.destroy();
        this.flickity = null;
      }
      this.initialized = false;
    }

    initWhenReady(attempts = 0) {
      if (typeof Flickity === 'undefined') {
        // Cap retries so a missing Flickity asset cannot freeze the page.
        if (attempts > 180) return;
        requestAnimationFrame(() => this.initWhenReady(attempts + 1));
        return;
      }
      if (this.initialized) return;
      this.initialized = true;

      this.bindArrows();
      this.initSlider();
      this.addEventListener('click', this.onOpenClick);
      this.addEventListener('click', this.onCloseClick);
      this.addEventListener('keydown', this.onKeydown);
      this.onResize = () => this.updateArrows();
      window.addEventListener('resize', this.onResize);
    }

    initSlider() {
      let options = {};
      try {
        options = JSON.parse(this.getAttribute('data-flickity-options')) || {};
      } catch (e) {
        options = {};
      }
      // contain + wrapAround fights cell sizing and can blow up the viewport
      if (options.wrapAround) options.contain = false;
      this.flickity = new Flickity(this.track, options);
      this.updateArrows();
      this.flickity.on('select', () => this.updateArrows());
      this.flickity.on('settle', () => this.updateArrows());
      this.scheduleReflow();
    }

    scheduleReflow() {
      const run = () => {
        if (!this.flickity) return;
        this.flickity.resize();
        this.updateArrows();
      };
      requestAnimationFrame(run);
      window.setTimeout(run, 100);
      window.setTimeout(run, 400);
      const imgs = this.track ? this.track.querySelectorAll('img') : [];
      imgs.forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', run, { once: true });
        img.addEventListener('error', run, { once: true });
      });
    }

    bindArrows() {
      this.nav = this.querySelector('.cf__nav');
      this.prevBtn = this.querySelector('[data-cf-prev]');
      this.nextBtn = this.querySelector('[data-cf-next]');
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!this.flickity || this.prevBtn.hasAttribute('disabled')) return;
          this.flickity.previous();
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!this.flickity || this.nextBtn.hasAttribute('disabled')) return;
          this.flickity.next();
        });
      }
    }

    updateArrows() {
      if (!this.flickity) return;

      const slides = this.flickity.slides || [];
      const cellCount = this.track
        ? this.track.querySelectorAll('.cf-cell').length
        : slides.length;
      const wrapAround = !!(this.flickity.options && this.flickity.options.wrapAround);

      // Hide arrows only when there is nothing to navigate.
      if (cellCount <= 1) {
        if (this.prevBtn) this.prevBtn.toggleAttribute('disabled', true);
        if (this.nextBtn) this.nextBtn.toggleAttribute('disabled', true);
        return;
      }

      // wrapAround keeps prev/next usable even when all cells fit the viewport
      // (common with 2 desktop looks) — otherwise both arrows get display:none.
      if (wrapAround) {
        if (this.prevBtn) this.prevBtn.toggleAttribute('disabled', false);
        if (this.nextBtn) this.nextBtn.toggleAttribute('disabled', false);
        return;
      }

      const index = this.flickity.selectedIndex;
      const slideableWidth = this.flickity.slideableWidth || 0;
      const viewportWidth =
        (this.flickity.size && this.flickity.size.innerWidth) || 0;
      const scrollable = slideableWidth > viewportWidth + 1;

      // With contain:true trailing slides can share the same target — treat
      // equal targets as the real start/end so arrows don't overshoot.
      const currentTarget = slides[index] ? slides[index].target : 0;
      const firstTarget = slides[0] ? slides[0].target : 0;
      const lastTarget = slides.length ? slides[slides.length - 1].target : 0;

      const atStart = !scrollable || index <= 0 || currentTarget === firstTarget;
      const atEnd =
        !scrollable ||
        index >= slides.length - 1 ||
        currentTarget === lastTarget;

      if (this.prevBtn) this.prevBtn.toggleAttribute('disabled', atStart);
      if (this.nextBtn) this.nextBtn.toggleAttribute('disabled', atEnd);
    }

    onOpenClick(event) {
      if (event.target.closest('[data-cf-stop]')) return;
      const trigger = event.target.closest('[data-cf-open]');
      if (!trigger || !this.contains(trigger)) return;

      const cell = trigger.closest('.cf-cell');
      if (!cell) return;
      this.openCell(cell, trigger);
    }

    onKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      const trigger = event.target.closest('[data-cf-open]');
      if (!trigger || !this.contains(trigger)) return;
      if (event.target.closest('[data-cf-stop]')) return;
      event.preventDefault();
      const cell = trigger.closest('.cf-cell');
      if (cell) this.openCell(cell, trigger);
    }

    onCloseClick(event) {
      const closeBtn = event.target.closest('[data-cf-close]');
      if (!closeBtn || !this.contains(closeBtn)) return;

      const cell = closeBtn.closest('.cf-cell');
      if (!cell) return;
      this.closeCell(cell);
    }

    openCell(cell, trigger) {
      this.querySelectorAll('.cf-cell.is-open').forEach((open) => {
        if (open !== cell) this.closeCell(open);
      });

      const panel = cell.querySelector('.cf-look__panel');
      if (panel) panel.hidden = false;
      cell.classList.add('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      this.reflow();
      this.maybeReinitLoox();
    }

    closeCell(cell) {
      const panel = cell.querySelector('.cf-look__panel');
      const trigger = cell.querySelector('[data-cf-open]');
      cell.classList.remove('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
      this.reflow();
    }

    reflow() {
      if (!this.flickity) return;
      this.flickity.resize();
      this.updateArrows();
    }

    maybeReinitLoox() {
      if (window.loox && typeof window.loox.reinit === 'function') {
        window.loox.reinit();
      }
    }
  }

  customElements.define('community-favorites', CommunityFavorites);
}
